import { useParams, Link } from 'react-router-dom';
import { useState, useEffect, useMemo } from 'react';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ComicCard from '@/components/feature/ComicCard';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/base/ScrollToTop';
import { comics, categories } from '@/mocks/comics';

export default function CategoryPage() {
  const { slug } = useParams<{ slug: string }>();
  const [activeSlug, setActiveSlug] = useState(slug || 'all');
  const [sortBy, setSortBy] = useState<'latest' | 'popular' | 'rating'>('latest');

  useEffect(() => {
    if (slug) {
      setActiveSlug(slug);
    }
  }, [slug]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [activeSlug]);

  const filteredComics = useMemo(() => {
    let result = activeSlug === 'all' ? [...comics] : comics.filter((c) => c.categorySlug === activeSlug);
    if (sortBy === 'latest') {
      result.sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime());
    } else if (sortBy === 'popular') {
      result.sort((a, b) => b.viewCount - a.viewCount);
    } else if (sortBy === 'rating') {
      result.sort((a, b) => b.rating - a.rating);
    }
    return result;
  }, [activeSlug, sortBy]);

  const activeCategory = categories.find((c) => c.slug === activeSlug);

  const schemaData = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        name: `${activeCategory?.name || '全部'}韩漫 - 虚空韩漫网`,
        url: `https://szlzjsm.cn/category/${activeSlug}`,
        description: `虚空韩漫网${activeCategory?.name || ''}漫画分类页面，浏览正版${activeCategory?.name || ''}韩漫大全，享受免费下拉式阅读体验。`,
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: '首页', item: 'https://szlzjsm.cn' },
          {
            '@type': 'ListItem',
            position: 2,
            name: activeCategory?.name || '全部漫画',
            item: `https://szlzjsm.cn/category/${activeSlug}`,
          },
        ],
      },
    ],
  };

  return (
    <>
      <Navbar />
      <main className="pt-16">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: activeCategory?.name || '全部漫画' },
            ]}
          />

          <div className="mb-8">
            <h1 className="text-2xl md:text-4xl font-heading font-bold text-foreground-900">
              {activeCategory?.name || '全部'}韩漫
            </h1>
            <p className="text-sm text-foreground-500 mt-2">
              虚空韩漫网为您精选{activeCategory?.name || '各类'}韩漫作品，正版授权，免费下拉式阅读
            </p>
          </div>

          {/* Category Tabs */}
          <div className="mb-6">
            <div className="flex items-center gap-2 overflow-x-auto pb-2">
              {categories.map((cat) => (
                <Link
                  key={cat.slug}
                  to={`/category/${cat.slug}`}
                  className={`px-4 py-2 rounded-full text-sm font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    activeSlug === cat.slug
                      ? 'bg-primary-500 text-background-50'
                      : 'bg-background-100 text-foreground-600 hover:bg-background-200'
                  }`}
                >
                  {cat.name}
                </Link>
              ))}
            </div>
          </div>

          {/* Sort Options */}
          <div className="flex items-center justify-between mb-6">
            <p className="text-sm text-foreground-500">
              共 <strong className="text-foreground-800">{filteredComics.length}</strong> 部作品
            </p>
            <div className="flex items-center gap-2 bg-background-100 rounded-full px-1 py-1">
              {([
                { value: 'latest', label: '最新' },
                { value: 'popular', label: '人气' },
                { value: 'rating', label: '评分' },
              ] as const).map((opt) => (
                <button
                  key={opt.value}
                  onClick={() => setSortBy(opt.value)}
                  className={`px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-300 whitespace-nowrap cursor-pointer ${
                    sortBy === opt.value
                      ? 'bg-background-50 text-foreground-900 shadow-sm'
                      : 'text-foreground-500 hover:text-foreground-700'
                  }`}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          {/* Comic Grid */}
          {filteredComics.length > 0 ? (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 md:gap-5 pb-14">
              {filteredComics.map((comic) => (
                <ComicCard key={comic.id} comic={comic} />
              ))}
            </div>
          ) : (
            <div className="py-20 text-center">
              <i className="ri-emotion-sad-line text-4xl text-foreground-300"></i>
              <p className="mt-4 text-foreground-500">该分类暂无漫画作品</p>
            </div>
          )}
        </div>
      </main>

      <Footer />
      <ScrollToTop />

      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(schemaData) }}
      />
    </>
  );
}